/* eslint-disable @typescript-eslint/no-restricted-imports */
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, Row, Col, Spin, Button } from "antd";
import { companyServiceApi } from "@/entities/api";
import { findUserById, findUserByIdOld } from "@/entities/user";
import { SectionFourDocTypeEnum, sectionFourDocTypeToEndpointMap } from "@/pages/journals/journals-section-fourth/ui";
import { oldBaseApi, userServiceApi } from "@/shared/api";
import { fallbackString, formatDate } from "@/shared/utils";
import { UserBubble } from "@shared/ui";
import "./info-dropdown.scss";


type InfoDropdownProps = {
  docType: SectionFourDocTypeEnum;
  docId: string;
  children?: ReactNode;
};

function formatDateTime(date?: string | null) {
  if (!date) return "-";
  try {
    const d = new Date(date);
    const dd = `${String(d.getDate()).padStart(2, "0")}.${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}.${d.getFullYear()}`;
    const hh = `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes(),
    ).padStart(2, "0")}`;
    return `${dd} ${hh} МСК`;
  } catch {
    return "-";
  }
}

export const InfoDropdown = ({
  docType,
  docId,
  children,
}: InfoDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const endpoint = sectionFourDocTypeToEndpointMap[docType];

  const { data: doc, isLoading } = useQuery({
    queryKey: ["docInfoSection4", endpoint, docId],
    queryFn: async () => {
      const res = await oldBaseApi.get(
        `/filestore/api/v1/${endpoint}/${docId}`,
      );
      return res.data as any;
    },
    enabled: Boolean(docId) && isOpen,
    staleTime: 5 * 60 * 1000,
  });
  

  const creatorId = doc?.creatorId as string | undefined;
  const creationDate = doc?.creationDate as string | undefined;

  const { data: legacyUser } = useQuery({
    queryKey: ["legacyUserSection4", creatorId],
    queryFn: async () => {
      const res = await findUserById(creatorId as string);
      return res;
    },
    enabled: Boolean(creatorId) && isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const authorFio = useMemo(() => {
    if (!legacyUser) return "-";
    const firstName = legacyUser?.userFirstName || "";
    const middleName = legacyUser?.userMiddleName || "";
    const lastName = legacyUser?.userSurname || "";
    return `${lastName} ${firstName} ${middleName}`.trim() || "-";
  }, [legacyUser]);

  const email = legacyUser?.userEmail as string | undefined;

  const { data: userByEmail } = useQuery({
    queryKey: ["userByEmailV2Section4", email],
    queryFn: async () => {
      const res = await userServiceApi.post(`/api/v1/users/`, {
        email,
        companyIds: [],
      });
      return res.data as any;
    },
    enabled: Boolean(email) && isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const authorCompanyId = (userByEmail?.userCompanies?.[0] ||
    userByEmail?.employment?.employmentId) as string | undefined;

  const { data: authorCompany } = useQuery({
    queryKey: ["authorCompanySection4", authorCompanyId],
    queryFn: async () => {
      const res = await companyServiceApi.get(`/companies/${authorCompanyId}`, {
        params: { idType: "companyId" },
      });
      return res.data as any;
    },
    enabled: Boolean(authorCompanyId) && isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const organizationAuthor =
    authorCompany?.shortName ||
    authorCompany?.name ||
    authorCompany?.fullName ||
    "-";

  const sheetValue = doc?.sheet ?? doc?.pdfPagesCount ?? "-";
  const [authorName, setAuthorName] = useState<string>("");
  useEffect(() => {
    if (creatorId) {
      findUserByIdOld(creatorId as string).then((user) => {
        const outName = `${user.middleName} ${user.firstName} `;
        if (outName) setAuthorName(outName);
      });
    }
  }, [creatorId]);

  const infoContent = (
    <div className="info-dropdown-panel">
      {isLoading ? (
        <div className="info-dropdown-panel__loading">
          <Spin size="small" />
        </div>
      ) : (
        <>
          <div className="info-dropdown-panel__section-title">Информация о документе</div>
          <Row gutter={[8, 8]}>
            <Col span={12} className="info-dropdown-panel__label">
              Дата выдачи
            </Col>
            <Col span={12}>{fallbackString(formatDate(doc?.issueDate))}</Col>

            <Col span={12} className="info-dropdown-panel__label">
              Организация
            </Col>
            <Col span={12}>{fallbackString(doc?.organization)}</Col>

            <Col span={12} className="info-dropdown-panel__label">
              Шифр
            </Col>
            <Col span={12}>{fallbackString(doc?.name)}</Col>

            <Col span={12} className="info-dropdown-panel__label">
              Лист
            </Col>
            <Col span={12}>{fallbackString(String(sheetValue))}</Col>
          </Row>

          <div className="info-dropdown-panel__section-title">Информация о загрузке</div>
          <Row gutter={[8, 8]}>
            <Col span={12} className="info-dropdown-panel__label">
              Загружено
            </Col>
            <Col span={12}>{formatDateTime(creationDate)}</Col>

            <Col span={12} className="info-dropdown-panel__label">
              Автор
            </Col>
            <Col span={12}>
              {creatorId ? (
                <UserBubble userId={creatorId}>
                  <Button style={{ padding: 0, height: "auto" }} type="link">
                    {fallbackString(authorName)}
                  </Button>
                </UserBubble>
              ) : (
                "-"
              )}
            </Col>

            <Col span={12} className="info-dropdown-panel__label">
              Организация автора
            </Col>
            <Col span={12}>{fallbackString(organizationAuthor)}</Col>
          </Row>

          <div className="info-dropdown-panel__section-title">
            Дополнительная информация к документу
          </div>
          <Row gutter={[8, 8]}>
            <Col span={12} className="info-dropdown-panel__label">
              Автор
            </Col>
            <Col span={12}>{fallbackString(authorFio)}</Col>

            <Col span={12} className="info-dropdown-panel__label">
              Комментарий
            </Col>
            <Col span={12}>
              {fallbackString(
                doc?.comment ??
                  doc?.description ??
                  doc?.info ??
                  doc?.information ??
                  "-",
              )}
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={infoContent}
      trigger={["click"]}
      placement="bottomLeft"
      onOpenChange={setIsOpen}
    >
      {children || (
        <div className="info-dropdown-trigger">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_14251_22400)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.93208 2.93305C4.22846 1.63669 6.02081 0.833984 7.99967 0.833984C9.97854 0.833984 11.7709 1.63669 13.0673 2.93305L12.7137 3.2866L13.0673 2.93305C14.3636 4.22943 15.1663 6.02178 15.1663 8.00065C15.1663 9.97952 14.3636 11.7719 13.0673 13.0682C11.7709 14.3646 9.97854 15.1673 7.99967 15.1673C6.0208 15.1673 4.22845 14.3646 2.93208 13.0682L3.28563 12.7147L2.93207 13.0682C1.63571 11.7719 0.833008 9.97952 0.833008 8.00065C0.833008 6.02178 1.63571 4.22943 2.93207 2.93305L2.93208 2.93305ZM7.99967 1.83398C6.29668 1.83398 4.75569 2.52366 3.63918 3.64016C2.52268 4.75667 1.83301 6.29765 1.83301 8.00065C1.83301 9.70365 2.52269 11.2446 3.63918 12.3611C4.75569 13.4776 6.29668 14.1673 7.99967 14.1673C9.70267 14.1673 11.2437 13.4776 12.3602 12.3611C13.4767 11.2446 14.1663 9.70365 14.1663 8.00065C14.1663 6.29765 13.4767 4.75667 12.3602 3.64016C11.2437 2.52366 9.70267 1.83398 7.99967 1.83398Z"
                fill="#333333"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.00033 4.33397C8.46056 4.33397 8.83366 4.70707 8.83366 5.16731C8.83366 5.62754 8.46056 6.00064 8.00033 6.00064C7.54009 6.00064 7.16699 5.62754 7.16699 5.16731C7.16699 4.70707 7.54009 4.33397 8.00033 4.33397Z"
                fill="#333333"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 7.5C7 7.22386 7.22386 7 7.5 7H8.16667C8.44281 7 8.66667 7.22386 8.66667 7.5L8.66667 11.5C8.66667 11.7761 8.44281 12 8.16667 12C7.89052 12 7.66667 11.7761 7.66667 11.5L7.66667 8H7.5C7.22386 8 7 7.77614 7 7.5Z"
                fill="#333333"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.5 11.5C6.5 11.2239 6.72386 11 7 11H9.33333C9.60948 11 9.83333 11.2239 9.83333 11.5C9.83333 11.7761 9.60948 12 9.33333 12H7C6.72386 12 6.5 11.7761 6.5 11.5Z"
                fill="#333333"
              />
            </g>
            <defs>
              <clipPath id="clip0_14251_22400">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      )}
    </Dropdown>
  );
};
